import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { MessagingProducts } from '~/types';
import { CREATE_ACTIVITY } from '~/services/conversations';

interface AddActivityDialogProps {
  open: boolean;
  onClose: () => void;
  agentId: string;
  searchValue?: string;
  onActivityCreated?: (activity: any) => void;
}

const AddActivityDialog: React.FC<AddActivityDialogProps> = ({
  open,
  onClose,
  agentId,
  searchValue = '',
  onActivityCreated,
}) => {
  const [t] = useTranslation('activity');
  const navigate = useNavigate();
  const dialogRef = useRef<HTMLDialogElement>(null);

  const [firstName, setFirstName] = useState<string>('');
  const [lastName, setLastName] = useState<string>('');
  const [phoneNumber, setPhoneNumber] = useState<string>(searchValue);
  const [phoneNumberError, setPhoneNumberError] = useState<string>('');

  // Create activity mutation
  const [createActivity, { loading: creatingActivity }] = useMutation(
    CREATE_ACTIVITY,
    {
      onCompleted: (data) => {
        const newActivity = data?.create_activity?.data.activity;
        console.log(data, newActivity.person_id);

        if (newActivity) {
          if (onActivityCreated) {
            onActivityCreated(newActivity);
          }
          navigate(
            `/agent/${agentId}/conversations/${newActivity.person_id}?activityId=${newActivity._id}`,
          );
        }
        console.log('Activity created: aca');
        onClose();
      },
      onError: (error) => {
        console.error('Error creating activity:', error);
      },
    },
  );

  // Update dialog visibility and reset fields when open prop changes
  useEffect(() => {
    if (open) {
      dialogRef.current?.showModal();
      setPhoneNumber(searchValue); // Initialize the phone number with searchValue
      setPhoneNumberError('');
    } else {
      setFirstName('');
      setLastName('');
      setPhoneNumber('');
      setPhoneNumberError('');
      dialogRef.current?.close();
    }
  }, [open, searchValue]);

  const validatePhoneNumber = (number: string) => {
    if (number.trim().length < 10) {
      setPhoneNumberError(t('phone_number_min_length'));
      return false;
    }
    setPhoneNumberError('');
    return true;
  };

  const handleCreateActivity = () => {
    if (!validatePhoneNumber(phoneNumber)) {
      return;
    }

    createActivity({
      variables: {
        data: {
          agent_id: agentId,
          channel_id: phoneNumber, // Use phoneNumber instead of searchValue
          messaging_product: MessagingProducts.WHATSAPP,
          first_name: firstName,
          last_name: lastName,
        },
      },
    });
  };

  const isFormValid = () => {
    return phoneNumber.trim().length >= 11 && firstName.trim() !== '';
  };

  if (!open) return null;

  return (
    <dialog ref={dialogRef} onClose={onClose} className="modal">
      <div className="modal-box flex max-w-md flex-col gap-2">
        <h2 className="mb-4 text-xl font-semibold text-gray-800">
          {t('add_new_activity')}
        </h2>

        <div className="mb-4">
          <label className="mb-2 block text-sm font-medium text-gray-700">
            {t('whatsapp_number')}
          </label>
          <input
            type="tel"
            className={`input-bordered input w-full ${
              phoneNumberError ? 'input-error' : ''
            }`}
            value={phoneNumber}
            onChange={(e) => {
              const value = e.target.value.replace(/[^0-9]/g, '');
              setPhoneNumber(value);
              if (value.trim().length >= 11) {
                setPhoneNumberError('');
              }
            }}
            placeholder={t('enter_whatsapp_number')}
            disabled={creatingActivity}
            minLength={11}
            pattern="[0-9]*"
            inputMode="numeric"
          />
          {phoneNumberError && (
            <div className="text-error mt-1 text-sm">{phoneNumberError}</div>
          )}
        </div>
        <div className="mb-3">
          <label className="mb-2 block text-sm font-medium text-gray-700">
            {t('first_name')}
          </label>
          <input
            type="text"
            className="input-bordered input w-full"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            placeholder={t('enter_first_name')}
            disabled={creatingActivity}
          />
        </div>

        <div className="mb-6">
          <label className="mb-2 block text-sm font-medium text-gray-700">
            {t('last_name')}
          </label>
          <input
            type="text"
            className="input-bordered input w-full"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            placeholder={t('enter_last_name')}
            disabled={creatingActivity}
          />
        </div>

        <div className="mt-4 flex justify-end space-x-2">
          <button
            className="btn btn-primary rounded-full"
            onClick={onClose}
            disabled={creatingActivity}
          >
            {t('cancel')}
          </button>
          <button
            className="btn btn-primary rounded-full"
            onClick={handleCreateActivity}
            disabled={!isFormValid() || creatingActivity}
          >
            {creatingActivity ? (
              <>
                <span className="loading loading-spinner loading-xs"></span>
                {t('creating')}
              </>
            ) : (
              t('create')
            )}
          </button>
        </div>
      </div>
      <form method="dialog" className="modal-backdrop">
        <button onClick={onClose}>close</button>
      </form>
    </dialog>
  );
};

export default AddActivityDialog;
